// Настройки по вкладкам (?tab=…): «Автономия и деньги», «Страховка и антифрод», «Интеграции», «Агент», «Общие»,
// «Система». Что в какой вкладке и в каком порядке — settings-nav.ts; здесь — как выглядит каждая карточка.
// Якорь карточки (#claude-access, #integrations…) открывает её вкладку и прокручивает к ней.
import { type ModelScope, type ModelSettings, windowLabel } from '@autoaff/protocol';
import {
  applyTheme,
  Button,
  Card,
  Chip,
  Meter,
  readTheme,
  Segmented,
  type ThemeChoice,
  time,
  toast,
  when,
} from '@autoaff/ui';
import { startRegistration } from '@simplewebauthn/browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link, useRouterState } from '@tanstack/react-router';
import { Code2, KeyRound, Laptop, LogOut, Moon, Plus, RefreshCw, Sun } from 'lucide-react';
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { type AgentStatus, api } from '../api';
import { ModelPicker } from '../chat/ModelPicker';
import { type ModelsState, useModels } from '../models';
import { useAgentStatus } from '../streams';
import { AntifraudCard } from './AntifraudCard';
import { AutonomyCard } from './AutonomyCard';
import { BackupCard } from './BackupCard';
import { BrowserCard } from './BrowserCard';
import { CapiCard } from './CapiCard';
import { ClaudeAccessCard } from './ClaudeAccessCard';
import { ClaudeCodeCard } from './ClaudeCodeCard';
import { PageHead } from './common';
import { GenCard } from './GenCard';
import { GuardCard } from './GuardCard';
import { InstallCard } from './InstallCard';
import { NetworkCard, ServicesCard } from './IntegrationsCard';
import { PandaImportCard } from './PandaImportCard';
import { PasswordBlock } from './PasswordBlock';
import { PingCard } from './PingCard';
import { PushCard } from './PushCard';
import {
  DEFAULT_TAB,
  SETTINGS_CARDS,
  SETTINGS_TABS,
  type SettingsCardId,
  type SettingsTab,
  tabOfAnchor,
} from './settings-nav';

const route = getRouteApi('/settings');

const SCOPES: Array<{ key: ModelScope; title: string; text: string }> = [
  {
    key: 'chat',
    title: 'Чат',
    text: 'Новые чаты с агентом. В самом чате модель меняется кнопкой в поле ввода.',
  },
  {
    key: 'rounds',
    title: 'Обходы по расписанию',
    text: 'Брифинги, обходы и итоги дня по расписанию.',
  },
  { key: 'creative', title: 'Креативы', text: 'Сценарии, раскадровки и сборка роликов.' },
];

function ModelsCard() {
  const q = useModels();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const save = async (next: ModelSettings) => {
    const prev = q.data?.settings;
    qc.setQueryData<ModelsState>(['models'], (d) => (d ? { ...d, settings: next } : d));
    try {
      await api('/api/models/settings', next, 'PUT');
    } catch (e) {
      toast((e as Error).message);
      qc.setQueryData<ModelsState>(['models'], (d) => (d && prev ? { ...d, settings: prev } : d));
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api<Pick<ModelsState, 'catalog'>>('/api/models/refresh', {});
      qc.setQueryData<ModelsState>(['models'], (d) => (d ? { ...d, catalog: r.catalog } : d));
      toast('Список моделей обновлён');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  };

  const s = q.data?.settings;
  const catalog = q.data?.catalog ?? null;
  return (
    <Card
      title="Модели агента"
      action={
        <Button size="sm" variant="ghost" icon={<RefreshCw />} onClick={refresh} disabled={refreshing}>
          {refreshing ? 'Спрашиваю Claude Code…' : 'Обновить список'}
        </Button>
      }
    >
      {q.isPending && (
        <div className="flex flex-col gap-3">
          <div className="skeleton" style={{ height: 40 }} />
          <div className="skeleton" style={{ height: 40 }} />
        </div>
      )}
      {q.isError && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="muted">Не удалось загрузить модели. {(q.error as Error).message}</span>
          <Button size="sm" onClick={() => q.refetch()}>
            Повторить
          </Button>
        </div>
      )}
      {s && (
        <>
          <div className="model-rows">
            <div className="model-row">
              <div>
                <b>По умолчанию</b>
                <p>Для всех запусков агента, если ниже не выбрано своё.</p>
              </div>
              <div className="acts">
                <ModelPicker
                  variant="field"
                  prefer="bottom"
                  value={s.default}
                  catalog={catalog}
                  label="Модель по умолчанию"
                  onChange={(c) => {
                    if (c) void save({ ...s, default: c });
                  }}
                />
              </div>
            </div>
            {SCOPES.map((sc) => {
              const own = s.scopes[sc.key];
              return (
                <div key={sc.key} className="model-row">
                  <div>
                    <b>{sc.title}</b>
                    <p>{sc.text}</p>
                  </div>
                  <div className="acts">
                    <ModelPicker
                      variant="field"
                      prefer="bottom"
                      value={own ?? s.default}
                      inherit={{ value: s.default, active: !own }}
                      catalog={catalog}
                      label={`Модель: ${sc.title.toLowerCase()}`}
                      onChange={(c) => void save({ ...s, scopes: { ...s.scopes, [sc.key]: c ?? undefined } })}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="muted" style={{ margin: '14px 0 0', fontSize: 'var(--fs-sm)' }}>
            Список моделей берётся у Claude Code: новые появятся здесь сами, но агент перейдёт на них, только
            если вы их выберете.
            {catalog
              ? ` Обновлён ${when(catalog.fetchedAt)}.`
              : ' Список появится, когда подключится сервис агента.'}
          </p>
        </>
      )}
    </Card>
  );
}

/** Лимиты подписки Claude: общие с ручной работой владельца в Claude Code. */
function SubscriptionCard() {
  const { status } = useAgentStatus();
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try {
      await api<AgentStatus>('/api/agent/usage/refresh', {});
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      title="Подписка Claude"
      action={
        <div className="flex items-center gap-2">
          {status?.runner ? (
            <Chip tone="success" dot>
              Агент подключён
            </Chip>
          ) : (
            <Chip tone="warning" dot>
              Агент не подключён
            </Chip>
          )}
          {status?.runner && status.usageAvailable !== false && (
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw />}
              onClick={refresh}
              disabled={busy}
              aria-label="Обновить лимиты"
            >
              <span className="hide-m">Обновить</span>
            </Button>
          )}
        </div>
      }
    >
      {status && status.subscription.length > 0 ? (
        <div className="flex flex-col gap-4">
          {status.subscription.map((w) => (
            <div key={w.kind} className="flex flex-col gap-2">
              <div className="flex justify-between gap-3" style={{ fontSize: 'var(--fs-sm)' }}>
                <span>{windowLabel(w.kind)}</span>
                <span className="muted tnum">
                  {Math.round(w.utilization * 100)}% · сброс {w.resetsAt ? when(w.resetsAt) : '—'}
                </span>
              </div>
              <Meter
                value={w.utilization}
                label={windowLabel(w.kind)}
                tone={w.utilization >= 0.9 ? 'danger' : w.utilization >= 0.75 ? 'warn' : undefined}
              />
            </div>
          ))}
          <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
            Эти лимиты общие с вашей ручной работой в Claude Code.{' '}
            {status.usageAvailable === false
              ? 'Со входом по токену Claude Code сообщает их только вместе с ответами агента — цифры обновляются после каждого ответа.'
              : 'Платформа проверяет их раз в 10 минут, не тратя лимит.'}
          </p>
        </div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          {!status?.runner
            ? 'Лимиты появятся, когда подключится сервис агента.'
            : status.usageAvailable === false
              ? 'Со входом по токену Claude Code сообщает лимиты только вместе с ответами агента — они появятся после первого ответа.'
              : 'Лимиты ещё не получены — нажмите «Обновить».'}
        </p>
      )}
    </Card>
  );
}

function AppearanceCard() {
  const [theme, setTheme] = useState<ThemeChoice>(readTheme());
  return (
    <Card title="Внешний вид">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <b style={{ fontWeight: 500 }}>Тема</b>
          <p className="muted" style={{ margin: '2px 0 0', fontSize: 'var(--fs-sm)' }}>
            «Как в системе» переключается вместе с телефоном или компьютером
          </p>
        </div>
        <Segmented
          label="Тема"
          value={theme}
          onChange={(v) => {
            setTheme(v);
            applyTheme(v);
          }}
          options={[
            { value: 'auto', label: 'Как в системе', icon: <Laptop /> },
            { value: 'light', label: 'Светлая', icon: <Sun /> },
            { value: 'dark', label: 'Тёмная', icon: <Moon /> },
          ]}
        />
      </div>
    </Card>
  );
}

interface Credential {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  backedUp: boolean;
}

/** Вход: пароль с кодом из Telegram, ключи доступа (запасной вход), выход на этом устройстве. */
function LoginCard() {
  const qc = useQueryClient();
  const creds = useQuery({
    queryKey: ['credentials'],
    queryFn: () => api<Credential[]>('/api/auth/credentials'),
  });

  const addKey = async () => {
    try {
      const options = await api<Parameters<typeof startRegistration>[0]['optionsJSON']>(
        '/api/auth/register/options',
        {
          name: 'Новое устройство',
        },
      );
      const response = await startRegistration({ optionsJSON: options });
      await api('/api/auth/register/verify', { response, name: navigator.platform || 'Устройство' });
      toast('Ключ добавлен — с этого устройства можно входить без пароля');
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    } catch (e) {
      toast((e as Error).name === 'NotAllowedError' ? 'Добавление ключа отменено' : (e as Error).message);
    }
  };

  const logout = async () => {
    await api('/api/auth/logout', {});
    window.location.reload();
  };

  return (
    <Card title="Вход">
      <PasswordBlock />
      <div className="flex items-center justify-between gap-2 flex-wrap" style={{ margin: '16px 0 4px' }}>
        <b style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>Ключи доступа — запасной вход</b>
        <Button size="sm" icon={<Plus />} onClick={addKey}>
          Добавить ключ
        </Button>
      </div>
      {creds.isPending && <div className="skeleton" style={{ height: 40 }} />}
      {creds.data?.length === 0 && (
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          Ключей пока нет. Ключ — вход отпечатком или лицом без пароля; пригодится, если не будет доступа к
          Telegram.
        </p>
      )}
      <div className="flex flex-col">
        {creds.data?.map((c) => (
          <div
            key={c.id}
            className="flex items-center gap-3"
            style={{ padding: '10px 0', borderTop: '1px solid var(--border)' }}
          >
            <KeyRound size={18} className="muted" />
            <div className="flex-1 min-w-0">
              <b style={{ fontWeight: 500 }}>{c.name}</b>
              <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                добавлен {when(c.createdAt)}
                {c.lastUsedAt ? ` · вход ${when(c.lastUsedAt)}` : ''}
              </div>
            </div>
            {c.backedUp && <Chip>синхронизирован</Chip>}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <Button variant="ghost" icon={<LogOut />} onClick={logout}>
          Выйти на этом устройстве
        </Button>
      </div>
    </Card>
  );
}

function DevCard() {
  return (
    <Card title="Разработка">
      <div className="inst-row">
        <p className="muted inst-text">
          Правки самой платформы агентом: отдельная копия кода, независимая проверка, снимки экранов «до /
          после», ваше решение, установка с наблюдением и откатом в одну кнопку.
        </p>
        <Link to="/dev" className="btn">
          <Code2 />
          Открыть
        </Link>
      </div>
    </Card>
  );
}

/** Вид каждой карточки; порядок и вкладки — в SETTINGS_CARDS. */
const CARD_VIEW: Record<SettingsCardId, () => ReactNode> = {
  autonomy: () => <AutonomyCard />,
  gen: () => <GenCard />,
  guard: () => <GuardCard />,
  antifraud: () => <AntifraudCard />,
  network: () => <NetworkCard />,
  capi: () => <CapiCard />,
  services: () => <ServicesCard />,
  'panda-import': () => <PandaImportCard />,
  models: () => <ModelsCard />,
  'claude-access': () => <ClaudeAccessCard />,
  subscription: () => <SubscriptionCard />,
  browser: () => <BrowserCard />,
  push: () => <PushCard />,
  install: () => <InstallCard />,
  login: () => <LoginCard />,
  appearance: () => <AppearanceCard />,
  backup: () => <BackupCard />,
  ping: () => <PingCard />,
  'claude-code': () => <ClaudeCodeCard />,
  dev: () => <DevCard />,
};

/** Строка вкладок; на телефоне листается, открытая вкладка всегда видна. */
function SettingsTabs({ active }: { active: SettingsTab }) {
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    const row = nav.current;
    if (!row) return;
    const show = () => {
      const cur = row.querySelector<HTMLElement>(`[data-tab="${active}"]`);
      if (!cur) return;
      const r = row.getBoundingClientRect();
      const c = cur.getBoundingClientRect();
      // Не влезает — в середину строки: соседние вкладки выглядывают с обеих сторон, видно, что строка листается
      if (c.left < r.left || c.right > r.right)
        row.scrollLeft += c.left + c.width / 2 - (r.left + r.width / 2);
    };
    show();
    // И ещё раз, когда вкладки меняют ширину: шрифт догружается позже, и открытая уехала бы за край
    const ro = new ResizeObserver(show);
    for (const a of row.querySelectorAll('a')) ro.observe(a);
    return () => ro.disconnect();
  }, [active]);
  return (
    <nav className="tabs" aria-label="Разделы настроек" ref={nav}>
      {SETTINGS_TABS.map((t) => {
        const Icon = t.icon;
        return (
          <Link
            key={t.id}
            to="/settings"
            search={{ tab: t.id }}
            data-tab={t.id}
            aria-current={t.id === active ? 'page' : undefined}
          >
            <Icon size={16} />
            {t.label}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Пришли по якорю карточки или строки (#claude-access, #telegram): прокрутить к ней с поправкой на плашку
 * алерта (она прилипает к верху). Карточки выше догружаются и меняют высоту — пока владелец сам
 * не прокрутил, держим цель у верха ещё пару секунд. Другая вкладка без якоря открывается с начала.
 */
function useScrollToAnchor(tab: SettingsTab, hash: string, body: RefObject<HTMLDivElement | null>) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: смена вкладки — повод прокрутить, хоть tab и не читается
  useEffect(() => {
    const main = document.getElementById('main');
    const target = hash ? document.getElementById(hash) : null;
    if (!main) return;
    if (!target || !body.current?.contains(target)) {
      main.scrollTop = 0;
      return;
    }
    let manual = false;
    const stop = () => {
      manual = true;
    };
    const align = () => {
      if (manual) return;
      const bars = [...main.querySelectorAll<HTMLElement>('.alert-bar')].map((b) => b.offsetHeight);
      const top = Math.max(0, ...bars) + 16;
      main.scrollTop += target.getBoundingClientRect().top - main.getBoundingClientRect().top - top;
    };
    align();
    const ro = new ResizeObserver(align);
    ro.observe(body.current);
    const timer = setTimeout(() => ro.disconnect(), 2500);
    main.addEventListener('wheel', stop, { passive: true });
    main.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
      main.removeEventListener('wheel', stop);
      main.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
  }, [tab, hash, body]);
}

export function SettingsPage() {
  const { tab: asked } = route.useSearch();
  const hash = useRouterState({ select: (s) => s.location.hash });
  // Якорь главнее: ссылка «/settings#claude-access» ведёт во вкладку своей карточки
  const tab = tabOfAnchor(hash) ?? asked ?? DEFAULT_TAB;
  const body = useRef<HTMLDivElement>(null);
  useScrollToAnchor(tab, hash, body);

  return (
    <>
      <PageHead title="Настройки" sub="Деньги и свобода агента, подключения, страховка и всё о платформе" />
      <SettingsTabs active={tab} />
      <div className="pc pc-read" ref={body}>
        {SETTINGS_CARDS.filter((c) => c.tab === tab).map((c) => (
          <div key={c.id} id={c.id} className="set-card">
            {CARD_VIEW[c.id]()}
          </div>
        ))}
        {tab === 'general' && (
          <p className="muted" style={{ fontSize: 'var(--fs-xs)', margin: 0 }}>
            Сейчас {time(new Date())} по Ташкенту.
          </p>
        )}
      </div>
    </>
  );
}
